export type PublicErrorResponse = {
    errorCode: string;
    message: string;
    correlationId: string;
    details?: Record<string, unknown>;
};

export const DEFINITELY_INTERNAL_ERROR_PATTERNS: readonly RegExp[] = [
    // SQL / DB markers
    /select\s+.+\s+from\s+/i,
    /insert\s+into\s+/i,
    /update\s+.+\s+set\s+/i,
    /delete\s+from\s+/i,
    /syntax error at or near/i,
    /sqlstate/i,
    /postgres(?:ql)?/i,
    /relation\s+"[^"]+"/i,
    /column\s+"[^"]+"\s+does not exist/i,
    /public\."[^"]+"/i,
    /knex/i,

    // Stack trace / file path markers
    /\n\s*at\s+[^\n]+:[0-9]+:[0-9]+/i,
    /\bnode_modules\b/i,
    /\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)+\.(?:ts|js|json|sql)\b/i,
];

export const DEFAULT_PUBLIC_MESSAGE = "Something went wrong. Please try again.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value && typeof value === "object" && !Array.isArray(value));

const normalizeString = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
};

const isSensitiveMessage = (message: string): boolean =>
    DEFINITELY_INTERNAL_ERROR_PATTERNS.some(pattern => pattern.test(message));

export const sanitizePublicMessage = (message: string): string => {
    const normalized = message.trim();
    if (!normalized) return DEFAULT_PUBLIC_MESSAGE;
    if (normalized.length > 220) return DEFAULT_PUBLIC_MESSAGE;
    if (isSensitiveMessage(normalized)) return DEFAULT_PUBLIC_MESSAGE;
    return normalized;
};

const statusToDefaultErrorCode = (status: number | undefined): string => {
    switch (status) {
        case 400:
            return "VALIDATION_ERROR";
        case 401:
            return "UNAUTHORIZED";
        case 403:
            return "FORBIDDEN";
        case 404:
            return "NOT_FOUND";
        case 409:
            return "CONFLICT";
        case 429:
            return "RATE_LIMITED";
        case 502:
        case 503:
            return "SERVICE_UNAVAILABLE";
        default:
            return "INTERNAL_ERROR";
    }
};

export const isPublicErrorResponse = (
    value: unknown,
): value is PublicErrorResponse => {
    if (!isRecord(value)) return false;
    return (
        typeof value.errorCode === "string" &&
        typeof value.message === "string" &&
        typeof value.correlationId === "string"
    );
};

export const coercePublicErrorResponse = (args: {
    status?: number;
    data: unknown;
    correlationId?: string | null;
}): PublicErrorResponse => {
    const correlationIdFromArgs = normalizeString(args.correlationId) ?? null;

    if (isPublicErrorResponse(args.data)) {
        const correlationId =
            correlationIdFromArgs ??
            normalizeString(args.data.correlationId) ??
            "unknown";
        return {
            errorCode:
                normalizeString(args.data.errorCode) ??
                statusToDefaultErrorCode(args.status),
            message: sanitizePublicMessage(args.data.message),
            correlationId,
            ...(isRecord(args.data.details) ? { details: args.data.details } : {}),
        };
    }

    if (isRecord(args.data)) {
        const legacyError = normalizeString(args.data.error);
        const legacyCode = normalizeString(args.data.code);
        const legacyMessage = normalizeString(args.data.message);

        const messageCandidate =
            legacyError ?? legacyMessage ?? DEFAULT_PUBLIC_MESSAGE;

        const correlationId =
            correlationIdFromArgs ??
            normalizeString(args.data.correlationId) ??
            "unknown";

        return {
            errorCode: legacyCode ?? statusToDefaultErrorCode(args.status),
            message: sanitizePublicMessage(messageCandidate),
            correlationId,
        };
    }

    if (typeof args.data === "string") {
        return {
            errorCode: statusToDefaultErrorCode(args.status),
            message: sanitizePublicMessage(args.data),
            correlationId: correlationIdFromArgs ?? "unknown",
        };
    }

    return {
        errorCode: statusToDefaultErrorCode(args.status),
        message: DEFAULT_PUBLIC_MESSAGE,
        correlationId: correlationIdFromArgs ?? "unknown",
    };
};

export const formatPublicErrorForDisplay = (
    publicError: PublicErrorResponse,
): string => {
    const correlationId = normalizeString(publicError.correlationId);
    if (!correlationId || correlationId === "unknown") {
        return publicError.message;
    }
    return `${publicError.message} (Ref: ${correlationId})`;
};

